import { close, toggleItem } from '@actions/sidebar.actions';
import { Component, HostBinding, OnInit } from '@angular/core';
import { RecursivePartial } from '@models/apollo';
import { CurrentUser } from '@models/user';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Storefront } from '@queries/generated';
import { CertificationChangeRequestService } from '@services/certification-change-request.service';
import { CurrentUserService } from '@services/current-user.service';
import { ProductCatalogService } from '@services/product-catalog.service';
import { StorefrontsService } from '@services/storefronts.service';
import { UserMenusService } from '@services/user-menus.service';
import { insertIf } from '@util/insert-if';
import { MenuItem } from 'primeng/api';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { State } from 'src/app/reducers';

export interface MenuBadges {
  pendingCertificationChangesCount: number;
}

@UntilDestroy()
@Component({
  selector: 'vnm-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit {
  @HostBinding('class.is-visible') isVisible = false;

  sidebarOpen$ = this.store.select((appState) => appState.sidebar.open);
  menuState$ = this.store.select((appState) => appState.sidebar.menuState);
  currentUser$ = this.currentUser.user$;
  currenStorefront$ = this.storefrontsService.current$;
  menuBadges$ = this.certificationChangeRequestService.count$.pipe(
    map((pendingCertificationChangesCount) => ({ pendingCertificationChangesCount }))
  );
  menuModel$ = combineLatest([this.menuState$, this.currentUser$, this.currenStorefront$, this.menuBadges$]).pipe(
    map(([menuState, user, storefront, menuBadges]) => ({
      menuState,
      user,
      storefront,
      menuBadges
    }))
  );

  user: CurrentUser;
  storefront: RecursivePartial<Storefront>;
  model: MenuItem[];
  menuState: { [key: string]: boolean };

  constructor(
    public currentUser: CurrentUserService,
    public userMenus: UserMenusService,
    public storefrontsService: StorefrontsService,
    public productCatalogService: ProductCatalogService,
    private certificationChangeRequestService: CertificationChangeRequestService,
    private store: Store<State>,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.menuModel$.pipe(untilDestroyed(this)).subscribe(({ menuState, user, storefront, menuBadges }) => {
      this.menuState = menuState;
      if (user) {
        this.model = [this.learnerItems(user, storefront), ...this.toolItems(user)];
        this.user = user;
        this.setupMainMenu(user, storefront, menuBadges);
      }
      if (storefront) {
        this.storefront = storefront;
      }
    });

    this.store
      .pipe(
        untilDestroyed(this),
        select((state) => state.sidebar.open)
      )
      .subscribe((open) => (this.isVisible = open));
  }

  onCloseSidebar(_) {
    this.store.dispatch(close());
  }

  private setupMainMenu(user: CurrentUser, currentStorefront: RecursivePartial<Storefront>, menuBadges: MenuBadges) {
    this.model = [this.learnerItems(user, currentStorefront)];

    this.model.push(...this.toolItems(user));

    if (user.can?.ilManager || user.can?.instructor) {
      this.model.unshift(this.iltItems(user));
    }

    if (user.can?.grade) {
      this.model.unshift(this.graderItems());
    }

    if (user.can?.trainingManager) {
      this.model.unshift(this.trainingManagerItems(user));
    }

    if (user.can?.author) {
      this.model.unshift(this.authorItems(user, menuBadges));
    }

    if (user.can?.vend || user.can?.resell) {
      this.model.unshift(this.storefrontOwnerItems(user));
    }
  }

  private graderItems(): MenuItem {
    return {
      id: 'gradingMenu',
      label: 'grading',
      icon: 'fa fa-fw fa-clipboard-check',
      automationId: 'grade-menu',
      expanded: this.menuState['grade-menu'],
      command: (event) => this.storeMenuItemState(event),
      items: [
        {
          label: 'manage-graded-assignments',
          routerLink: ['grading', 'manage-assignments']
        }
      ]
    };
  }

  private iltItems(user: CurrentUser): MenuItem {
    return {
      id: 'ilMenu',
      label: 'ilt',
      icon: 'fa fa-fw fa-chalkboard-teacher',
      automationId: 'il-menu',
      expanded: this.menuState['il-menu'],
      command: (event) => this.storeMenuItemState(event),
      items: [
        {
          label: 'il-dashboard',
          routerLink: ['ilt'],
          routerLinkActiveOptions: { exact: true }
        },
        {
          label: 'manage-il-classes',
          routerLink: ['ilt', 'manage-classes']
        },
        ...insertIf(
          user?.can?.ilManager,
          {
            label: 'manage-il-courses',
            routerLink: ['ilt', 'manage-courses']
          },
          {
            label: 'manage-il-locations',
            routerLink: ['ilt', 'manage-locations']
          },
          {
            label: 'manage-il-instructors',
            routerLink: ['ilt', 'manage-instructors']
          },
          {
            label: 'reports',
            routerLink: ['ilt', 'reports']
          }
        )
      ]
    };
  }

  private storefrontOwnerItems(user: CurrentUser): MenuItem {
    return {
      id: 'sellingMenu',
      label: 'selling',
      icon: 'fa fa-fw fa-store-alt',
      automationId: 'vendor-menu',
      expanded: this.menuState['vendor-menu'],
      command: (event) => this.storeMenuItemState(event),
      items: [
        {
          label: 'selling-dashboard',
          routerLink: ['selling'],
          routerLinkActiveOptions: { exact: true }
        },
        {
          label: 'manage-storefronts',
          routerLink: ['selling', 'storefronts']
        },
        {
          label: 'manage-subscriptions',
          routerLink: ['selling', 'subscriptions']
        },
        {
          label: 'reports',
          routerLink: ['selling', 'reports']
        },
        {
          label: 'shared-products',
          routerLink: ['selling', 'shared-products']
        }
      ]
    };
  }

  private changeRequestsItem(menuBadges: MenuBadges): MenuItem {
    const count = menuBadges.pendingCertificationChangesCount;
    const label =
      count > 0
        ? this.translateService.instant('sidebar.change-requests-with-pending', {
            count: menuBadges.pendingCertificationChangesCount
          })
        : this.translateService.instant('sidebar.change-requests');

    return {
      label: label,
      routerLink: ['authoring', 'change-requests'],
      escape: false,
      styleClass: count > 0 ? 'p-menuitem-badge' : ''
    };
  }

  private authorItems(user: CurrentUser, menuBadges: MenuBadges): MenuItem {
    return {
      id: 'authoringMenu',
      label: 'authoring',
      icon: 'fa fa-fw fa-graduation-cap',
      automationId: 'author-menu',
      expanded: this.menuState['author-menu'],
      command: (event) => this.storeMenuItemState(event),
      items: [
        {
          label: 'manage-courses',
          routerLink: ['authoring', 'courses']
        },
        {
          label: 'manage-exams',
          routerLink: ['authoring', 'exams']
        },
        {
          label: 'certification-templates',
          routerLink: ['authoring', 'certifications']
        },
        this.changeRequestsItem(menuBadges),
        {
          label: 'manage-verifications',
          routerLink: ['authoring', 'verifications']
        },
        {
          label: 'reports',
          routerLink: ['authoring', 'reports']
        },
        ...insertIf(user?.can?.survey, {
          label: 'manage-surveys',
          routerLink: ['authoring', 'surveys']
        })
      ]
    };
  }

  private trainingManagerItems(user: CurrentUser): MenuItem {
    return {
      id: 'trainingMenu',
      label: 'training',
      icon: 'fa fa-fw fa-users',
      automationId: 'training-manager-menu',
      expanded: this.menuState['training-manager-menu'],
      command: (event) => this.storeMenuItemState(event),
      items: [
        {
          label: 'training-dashboard',
          routerLink: ['training'],
          routerLinkActiveOptions: { exact: true }
        },
        {
          label: 'manage-learners',
          routerLink: ['training', 'learners']
        },
        {
          label: 'manage-certifications',
          routerLink: ['training', 'certifications']
        },
        {
          label: 'manage-licenses',
          routerLink: ['training', 'licenses']
        },
        // {
        //   label: 'manage-training-subscriptions',
        //   routerLink: ['training', 'subscriptions']
        // },
        {
          label: 'manage-verifications',
          routerLink: ['training', 'verifications']
        },
        ...insertIf(user.can?.manageTrainingMatrices, {
          label: 'manage-training-matrices',
          routerLink: ['training', 'matrices']
        }),
        {
          label: 'manage-training-settings',
          routerLink: ['training', 'settings']
        },
        {
          label: 'reports',
          routerLink: ['training', 'reports']
          // url: '/app/reports/trainings'
        }
      ]
    };
  }

  private learnerItems(user: CurrentUser, currentStorefront: RecursivePartial<Storefront>): MenuItem {
    return {
      id: 'learningMenu',
      label: 'learning',
      icon: 'fa fa-fw fa-user',
      automationId: 'learner-menu',
      expanded: this.menuState['learner-menu'],
      command: (event) => this.storeMenuItemState(event),
      items: [
        {
          label: 'my-courses',
          routerLink: ['learning', 'courses']
        },
        {
          label: 'my-certifications',
          routerLink: ['learning', 'certifications']
        },
        ...insertIf(currentStorefront?.hasSubscriptions || user?.hasStripeSubscriptions, {
          label: 'my-subscriptions',
          routerLink: ['learning', 'subscriptions']
        })
      ]
    };
  }

  private toolItems(user: CurrentUser): MenuItem[] {
    const tools: MenuItem[] = [];

    if (user.can.sidekiq) {
      tools.push({
        label: 'sidekiq',
        url: '/app/sidekiq'
      });
    }

    if (user.can.admin) {
      tools.push({
        label: 'admin',
        url: '/admin'
      });
    }

    if (tools.length === 0) {
      return [];
    }

    return [
      {
        label: 'tools',
        icon: 'fa fa-fw fa-tools',
        automationId: 'tools-menu',
        expanded: this.menuState['tools-menu'],
        command: (event) => this.storeMenuItemState(event),
        items: tools
      }
    ];
  }

  private storeMenuItemState(event: any) {
    const { item } = event;
    const menuItem = { [item.automationId]: item.expanded };
    this.menuState = {
      ...this.menuState,
      ...menuItem
    };
    this.store.dispatch(toggleItem({ item: menuItem }));
  }
}
